/**
 * This the main document viewer component for StanfordTax used to view, annotate, and manipulate PDF documents.
 *
 * It comes with a toolbar for similar to what you would get in a typical PDF viewer. For example:
 * - Zoom in/out
 * - Text selection
 * - Text, rectangle, and stamp annotations
 * - Commenting with a comment sidebar
 *
 * It also has some tax specific features such as:
 * - Split bundled document
 * - Mark as prepared and reviewed
 */
import {
  DocumentViewer,
  DocumentViewerMessage,
  DocumentViewerProvider,
  DocumentViewerState,
  selActiveAnnotation,
  ToolMessage,
  useDocumentViewer,
  useDocumentViewerState,
  useInitDocumentViewer
} from '@st/document-viewer-kit'
import { selAnnotations } from '@st/document-viewer-kit/src/document-viewer-module'
import { DocumentAnnotation, DocumentPermission, DocumentSplit, STDocument } from '@st/sdk'
import {
  Button,
  CalculatorIcon,
  CheckIcon,
  CommentIcon,
  CursorIcon,
  LinkIcon,
  RectangleIcon,
  StampIcon,
  TextIcon,
  ToggleButton,
  Toolbar,
  ToolbarSection,
  ToolbarSectionDivider,
  ZoomInIcon,
  ZoomOutIcon
} from '@st/theme'
import { isNotEmpty } from '@st/util/json-value'
import { clsx } from 'clsx'
import { ReactNode } from 'react'
import { useAuthState } from '~/features/app-hooks'
import { STFolderMessage, stFolderModule } from '~/features/st-folder-viewer/st-folder-module'
import { useProcessState } from '~/store'
import { ToolState } from './annotations'
import { AnnotationsProvider } from './annotations-context'
import { SplitDocumentBanner } from './document-splitting/split-document-banner'
import { SplitDocumentPageDivider } from './document-splitting/split-document-page-divider'
import { ST_DOC_VIEWER_PLUGINS } from './plugins'
import { CommentsSidebar, isCommentAnnotation } from './plugins/comment/ui'
import { RectangleToolEditor } from './plugins/rectangle'
import { StatusStampsDocumentBanner } from './plugins/sign-offs/ui'
import { StampToolEditor } from './plugins/stamp'
import { CheckmarkToolEditor } from './plugins/checkmark'
import { CrossRefToolEditor } from './plugins/cross-references'
import { CHECKMARK_TICK_STAMP } from './plugins/stamp/templates'
import { TextToolEditor } from './plugins/text'
import { STDocumentPageToolbar } from './st-document-page-toolbar'

const SHOW_TOOLBAR = true

type Props = {
  doc: STDocument
  folderId: string
  send: (message: STFolderMessage) => void
  onDeletePage: (pageNumber: number) => void
  onRestorePage: (pageNumber: number) => void
  onAddSplit: (afterPageNumber: number) => void
  onRemoveSplit: (afterPageNumber: number) => void
  onRotatePage: (pageNumber: number, rotation: number) => void
}
export function STPDFDocViewer({
  doc,
  folderId,
  send,
  onDeletePage,
  onRestorePage,
  onAddSplit,
  onRemoveSplit,
  onRotatePage
}: Props) {
  const { user } = useAuthState()

  const users = useProcessState(stFolderModule, (s) => s.folderState?.users ?? [])
  const documentPermissions = doc.permissions ?? []
  const canSplit =
    documentPermissions.includes('split') &&
    // we also check for the document type because someone may correct the category after the fact
    // perhaps it wasn't actually a bundled document
    doc.documentTypeId == 'bundled_document'

  return (
    <AnnotationsProvider currentUser={user} users={users}>
      <DocumentViewerProvider>
        <STNewPDFDocViewerInner
          src={doc.pdfPath!}
          folderId={folderId}
          documentId={doc.id}
          banner={
            canSplit ? (
              <SplitDocumentBanner doc={doc} />
            ) : (
              SHOW_TOOLBAR && <StatusStampsDocumentBanner title={doc.name} />
            )
          }
          annotations={doc.annotations ?? {}}
          send={send}
          documentPermissions={documentPermissions}
          documentSplits={doc.splits}
          deletedPageNumbers={doc.deletedPageNumbers ?? []}
          pageRotations={doc.pageRotations}
          onDeletePage={onDeletePage}
          onRestorePage={onRestorePage}
          onAddSplit={onAddSplit}
          onRemoveSplit={onRemoveSplit}
          onRotatePage={onRotatePage}
        />
      </DocumentViewerProvider>
    </AnnotationsProvider>
  )
}

function STNewPDFDocViewerInner({
  src,
  folderId,
  documentId,
  banner,
  annotations,
  send,
  documentPermissions,
  documentSplits,
  deletedPageNumbers,
  pageRotations,
  onDeletePage,
  onRestorePage,
  onAddSplit,
  onRemoveSplit,
  onRotatePage
}: {
  src: string
  folderId: string
  documentId: string
  banner?: ReactNode
  annotations: Record<string, DocumentAnnotation>
  send: (message: STFolderMessage) => void
  documentPermissions: DocumentPermission[]
  documentSplits: DocumentSplit[]
  deletedPageNumbers: number[]
  pageRotations: Record<string, number>
  onDeletePage: (pageNumber: number) => void
  onRestorePage: (pageNumber: number) => void
  onAddSplit: (afterPageNumber: number) => void
  onRemoveSplit: (afterPageNumber: number) => void
  onRotatePage: (pageNumber: number, rotation: number) => void
}) {
  useInitDocumentViewer({
    annotations: annotations,
    plugins: ST_DOC_VIEWER_PLUGINS,
    initialTool: 'select'
  })

  const documentViewer = useDocumentViewer()
  const state = useDocumentViewerState((s) => s)
  const hasComments = useDocumentViewerState((s) =>
    isNotEmpty(selAnnotations(s, isCommentAnnotation))
  )

  return (
    <DocViewerScaffold
      toolbar={
        <PDFDocToolbar send={documentViewer.send} state={state} onDeletePage={onDeletePage} />
      }
      banner={banner}
      sidebar={hasComments ? <CommentsSidebar /> : undefined}
    >
      {src && (
        <DocumentViewer
          src={src}
          plugins={ST_DOC_VIEWER_PLUGINS}
          annotations={annotations}
          pageRotations={pageRotations}
          deletedPageNumbers={deletedPageNumbers}
          PageDivider={
            documentPermissions.includes('split')
              ? (p) => (
                  <SplitDocumentPageDivider
                    {...p}
                    documentSplits={documentSplits}
                    onAddSplit={onAddSplit}
                    onRemoveSplit={onRemoveSplit}
                  />
                )
              : undefined
          }
          PageToolbar={(p) => (
            <STDocumentPageToolbar
              {...p}
              permissions={documentPermissions}
              isDeleted={deletedPageNumbers.includes(p.page.pageNumber)}
              rotation={pageRotations[p.page.pageNumber] || 0}
              onDeletePage={onDeletePage}
              onRestorePage={onRestorePage}
              onRotatePage={(pageNumber, rotation) => onRotatePage(pageNumber, rotation)}
            />
          )}
          createAnnotation={(a: any) => {
            const annotation = a as DocumentAnnotation
            send({
              type: 'request',
              request: {
                type: 'folders/addAnnotationToDocument',
                annotation: annotation,
                folderId: folderId,
                documentId: documentId
              }
            })
          }}
          updateAnnotation={(a: any) => {
            const annotation = a as DocumentAnnotation
            send({
              type: 'request',
              request: {
                type: 'folders/updateDocumentAnnotation',
                annotation: annotation,
                folderId: folderId,
                documentId: documentId
              }
            })
          }}
          deleteAnnotation={(message) => {
            send({
              type: 'request',
              request: {
                type: 'folders/deleteDocumentAnnotation',
                folderId: folderId,
                documentId: documentId,
                annotationId: message
              }
            })
          }}
        />
      )}
    </DocViewerScaffold>
  )
}

/**
 * Handles the layout for a document viewer which includes the scrollable document itself
 * and optional surrounding elements like a toolbar, banner, and right sidebar.
 */
export function DocViewerScaffold({
  toolbar,
  banner,
  children,
  sidebar
}: {
  toolbar?: ReactNode
  banner?: ReactNode
  children: ReactNode
  sidebar?: ReactNode
}) {
  return (
    <div
      className={clsx('grid overflow-hidden', {
        'grid-rows-[50px_56px_auto]': toolbar && banner,
        'grid-rows-[50px_auto]': toolbar && !banner,
        'grid-rows-[56px_auto]': !toolbar && banner,
        'grid-rows-[auto]': !toolbar && !banner
      })}
    >
      {toolbar}
      {banner}
      <div
        className={clsx('grid overflow-hidden', {
          'grid-cols-[1fr_18rem]': sidebar,
          'grid-cols-[1fr]': !sidebar
        })}
      >
        {children}
        {sidebar}
      </div>
    </div>
  )
}

function PDFDocToolbar({
  send,
  state,
  onDeletePage: _onDeletePage
}: {
  send: (message: DocumentViewerMessage) => void
  state: DocumentViewerState
  onDeletePage: (pageNumber: number) => void
}) {
  const zoomLevel = state.zoom
  const tool = state.selectedToolName
    ? (state.toolStates[state.selectedToolName] as ToolState)
    : undefined
  const activeAnnotation = selActiveAnnotation(state) as DocumentAnnotation | undefined

  return (
    <Toolbar className="gap-6">
      <ToolbarSection>
        <Button
          variant="subtle"
          disabled={resolveZoomLevel(zoomLevel, -1) === undefined}
          onClick={() => send({ type: 'zoom', level: resolveZoomLevel(zoomLevel, -1)! })}
          aria-label="Zoom out"
        >
          <ZoomOutIcon className="size-6" />
        </Button>
        <Button
          variant="subtle"
          disabled={resolveZoomLevel(zoomLevel, 1) === undefined}
          onClick={() => send({ type: 'zoom', level: resolveZoomLevel(zoomLevel, 1)! })}
          aria-label="Zoom in"
        >
          <ZoomInIcon className="size-6" />
        </Button>
      </ToolbarSection>

      {SHOW_TOOLBAR && <ToolbarSectionDivider />}

      {SHOW_TOOLBAR && (
        <ToolbarSection>
          <ToggleButton
            isSelected={tool?.name == 'select'}
            onClick={() => {
              send({ type: 'selectTool', toolName: 'select' })
            }}
            aria-label="Select tool"
          >
            <CursorIcon className="size-6" />
          </ToggleButton>

          <ToggleButton
            isSelected={tool?.name == 'text'}
            onClick={() => {
              send({ type: 'selectTool', toolName: 'text' })
            }}
            aria-label="Text tool"
          >
            <TextIcon className="size-6" />
          </ToggleButton>

          <ToggleButton
            isSelected={tool?.name == 'checkmark'}
            onClick={() => {
              send({ type: 'selectTool', toolName: 'checkmark' })
            }}
            aria-label="Checkmark tool"
          >
            <CheckIcon className="size-6" />
          </ToggleButton>

          <ToggleButton
            isSelected={tool?.name == 'stamp'}
            onClick={() => {
              send({ type: 'selectTool', toolName: 'stamp' })
            }}
            aria-label="Stamp tool"
          >
            <StampIcon className="size-6" />
          </ToggleButton>

          <ToggleButton
            isSelected={tool?.name == 'cross_references'}
            onClick={() => {
              send({ type: 'selectTool', toolName: 'cross_references' })
            }}
            aria-label="Reference stamp tool"
          >
            <LinkIcon className="size-6" />
          </ToggleButton>

          <ToggleButton
            isSelected={tool?.name == 'rectangle'}
            onClick={() => {
              send({ type: 'selectTool', toolName: 'rectangle' })
            }}
            aria-label="Rectangle tool"
          >
            <RectangleIcon className="size-6" />
          </ToggleButton>

          <ToggleButton
            isSelected={tool?.name == 'calculator_tape'}
            onClick={() => {
              send({ type: 'selectTool', toolName: 'calculator_tape' })
            }}
            aria-label="Calculator tape tool"
          >
            <CalculatorIcon className="size-6" />
          </ToggleButton>

          <ToggleButton
            isSelected={tool?.name == 'comment'}
            onClick={() => {
              send({ type: 'selectTool', toolName: 'comment' })
            }}
            aria-label="Comment tool"
          >
            <CommentIcon className="size-6" />
          </ToggleButton>
        </ToolbarSection>
      )}

      {SHOW_TOOLBAR && <ToolbarSectionDivider />}

      {SHOW_TOOLBAR && (
        <ToolbarSection>
          {tool && <SelectedTool tool={tool} activeAnnotation={activeAnnotation} send={send} />}
        </ToolbarSection>
      )}
    </Toolbar>
  )
}

function SelectedTool({
  tool,
  activeAnnotation,
  send
}: {
  tool: ToolState
  activeAnnotation: DocumentAnnotation | undefined
  send: (message: ToolMessage<DocumentAnnotation, ToolState>) => void
}) {
  switch (tool.name) {
    case 'rectangle':
      return (
        <RectangleToolEditor
          toolState={tool}
          activeAnnotation={activeAnnotation?.type === 'rectangle' ? activeAnnotation : undefined}
          send={send}
        />
      )
    case 'text':
      return (
        <TextToolEditor
          toolState={tool}
          activeAnnotation={activeAnnotation?.type === 'text' ? activeAnnotation : undefined}
          send={send}
        />
      )
    case 'stamp':
      return (
        <StampToolEditor
          toolState={tool}
          send={send}
          activeAnnotation={activeAnnotation?.type === 'stamp' ? activeAnnotation : undefined}
        />
      )
    case 'checkmark':
      return (
        <CheckmarkToolEditor
          toolState={tool}
          send={send}
          activeAnnotation={activeAnnotation?.type === 'stamp' ? activeAnnotation : undefined}
        />
      )
    case 'cross_references':
      return (
        <CrossRefToolEditor
          toolState={tool}
          send={send}
          activeAnnotation={activeAnnotation?.type === 'stamp' ? activeAnnotation : undefined}
        />
      )
    case 'comment':
    case 'select':
    case 'calculator_tape':
    case 'sign_offs':
      return null
  }
}

const ZOOM_OPTIONS: number[] = [8.33, 12.5, 25, 50, 75, 100, 125, 150, 200, 400, 800].map(
  (x) => x / 100
)

function resolveZoomLevel(currentZoomLevel: number, delta: number): number | undefined {
  const index = ZOOM_OPTIONS.indexOf(currentZoomLevel)
  return ZOOM_OPTIONS[index + delta]
}
